import { useState } from 'react';
import axios from 'axios';
import { useHandleApiErrors } from './useHandleApiErrors';
import { ApiError, ApiType, ErrorType } from 'types';
import { useAppContextSelector } from './useAppContext';
import { getAuthHeader } from 'utils';

export type ApplicationStatus =
  | 'CREATED'
  | 'PENDING'
  | 'SAVED'
  | 'BIOMETRICS_STARTED'
  | 'BIOMETRICS_COMPLETED'
  | 'BIOMETRICS_CANCELLED'
  | 'APPROVED'
  | 'DECLINED'
  | 'QUOTE_DECLINE'
  | 'QUOTE_INITIAL'
  | 'REFERRED'
  | 'SERVICE_UNAVAILABLE'
  | 'CONDITIONALLY_APPROVED'
  | 'PENDING_CUSTOMER_ACCEPTANCE'
  | 'PENDING_APPROVAL'
  | 'CANCELLED'
  | 'POI_CHECK_PENDING'
  | 'POI_CHECK_REQUIRED'
  | 'POI_BIO_CHECK_REQUIRED'
  | 'IDV_CHECK_REQUIRED'
  | 'IDV_CHECK_PENDING'
  | 'CCR_PENDING'
  | 'CCR_REQUIRED'
  | 'BIOMETRICS_CHECK_REQUIRED';

export type ApplicationStatusReason =
  | 'UNKNOWN'
  | 'PROOF_OF_ADDRESS'
  | 'ANTI_MONEY_LAUNDERING'
  | 'PROOF_OF_INCOME'
  | 'ANTI_MONEY_LAUNDERING_AND_PROOF_OF_INCOME'
  | 'PROOF_OF_ADDRESS_AND_PROOF_OF_INCOME'
  | 'SERVICE_UNAVAILABLE'
  | 'QD';

export type GetApplicationResponse = {
  status: ApplicationStatus;
  detailedStatus: {
    status: ApplicationStatus;
    details: {
      reason?: ApplicationStatusReason;
    };
  };
  externalApplicationId: string;
  accountNumber?: string;
  approvedLimit?: number;
  firstName?: string;
  lastName?: string;
};

type UseGetApplication = {
  loading: boolean;
  getApplication: (applicationId: string) => Promise<GetApplicationResponse | undefined>;
};

type UseGetApplicationArgs = {
  onSuccess?: (data: GetApplicationResponse) => void;
  onError?: (error: ApiError) => void;
};

export const useGetApplication = ({ onSuccess, onError }: UseGetApplicationArgs = {}): UseGetApplication => {
  const [loading, setLoading] = useState(false);
  const { handleError } = useHandleApiErrors(ApiType.axios, onError);
  const { accessToken } = useAppContextSelector((ctx) => ctx.state);

  const getApplication = async (applicationId: string): Promise<GetApplicationResponse | undefined> => {
    const url = process.env.REACT_APP_APPLY_API_URL as string;
    const options = {
      headers: {
        Authorization: getAuthHeader(accessToken),
        Accept: 'application/json',
      },
    };

    try {
      setLoading(true);

      const response = await axios.get<GetApplicationResponse>(
        `${url}/applications/${applicationId}`,
        options,
      );

      setLoading(false);

      const data = response.data;
      onSuccess && onSuccess(data);
      return data;
    } catch (error) {
      setLoading(false);
      handleError({ type: ErrorType.AXIOS_NETWORK_ERROR, error });
      return undefined;
    }
  };

  return { loading, getApplication };
};
